import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
    X, Send, Sparkles, Heart, MessageCircle, Share2, Bookmark,
    Play, Music, MoreHorizontal, User, Search, Camera,
    Image, Video, ThumbsUp, ThumbsDown, ChevronLeft, ChevronRight
} from 'lucide-react';

// ========================================
// YOUTUBE COMPONENTS
// ========================================

// Thumbnail Templates - Placeholder for Real Images
export const THUMBNAIL_TEMPLATES = [
    {
        id: 'template-1',
        name: 'Bold Impact',
        preview: (headshots) => (
            <div className="w-full h-full relative bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center">
                <div className="text-center">
                    <div className="w-16 h-16 mx-auto mb-3 rounded-xl bg-gray-300 flex items-center justify-center">
                        <Image size={32} className="text-gray-400" />
                    </div>
                    <p className="text-gray-400 font-semibold text-sm">Template 1</p>
                </div>
            </div>
        )
    },
    {
        id: 'template-2',
        name: 'Split Screen',
        preview: (headshots) => (
            <div className="w-full h-full relative bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center">
                <div className="text-center">
                    <div className="w-16 h-16 mx-auto mb-3 rounded-xl bg-gray-300 flex items-center justify-center">
                        <Image size={32} className="text-gray-400" />
                    </div>
                    <p className="text-gray-400 font-semibold text-sm">Template 2</p>
                </div>
            </div>
        )
    },
    {
        id: 'template-3',
        name: 'Quote Style',
        preview: (headshots) => (
            <div className="w-full h-full relative bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center">
                <div className="text-center">
                    <div className="w-16 h-16 mx-auto mb-3 rounded-xl bg-gray-300 flex items-center justify-center">
                        <Image size={32} className="text-gray-400" />
                    </div>
                    <p className="text-gray-400 font-semibold text-sm">Template 3</p>
                </div>
            </div>
        )
    },
    {
        id: 'template-4',
        name: 'Minimalist',
        preview: (headshots) => (
            <div className="w-full h-full relative bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center">
                <div className="text-center">
                    <div className="w-16 h-16 mx-auto mb-3 rounded-xl bg-gray-300 flex items-center justify-center">
                        <Image size={32} className="text-gray-400" />
                    </div>
                    <p className="text-gray-400 font-semibold text-sm">Template 4</p>
                </div>
            </div>
        )
    },
    {
        id: 'template-5',
        name: 'Vibrant',
        preview: (headshots) => (
            <div className="w-full h-full relative bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center">
                <div className="text-center">
                    <div className="w-16 h-16 mx-auto mb-3 rounded-xl bg-gray-300 flex items-center justify-center">
                        <Image size={32} className="text-gray-400" />
                    </div>
                    <p className="text-gray-400 font-semibold text-sm">Template 5</p>
                </div>
            </div>
        )
    }
];

// YouTube Thumbnail Preview Component
export const YouTubeThumbnailPreview = ({ selectedTemplate = 0, headshots, onTemplateChange }) => {
    const template = THUMBNAIL_TEMPLATES[selectedTemplate];

    return (
        <div className="w-full h-full flex flex-col">
            {/* Template Preview - 16:9 Aspect Ratio */}
            <div className="flex-1 relative bg-gray-100 rounded-2xl overflow-hidden shadow-2xl border-2 border-gray-200">
                {template.preview(headshots)}
            </div>

            {/* Template Navigation */}
            <div className="mt-4 flex items-center justify-between gap-3">
                <button
                    onClick={() => onTemplateChange((selectedTemplate - 1 + THUMBNAIL_TEMPLATES.length) % THUMBNAIL_TEMPLATES.length)}
                    className="p-2.5 bg-white border-2 border-gray-200 rounded-xl hover:bg-gray-50 hover:border-indigo-300 transition-all"
                >
                    <ChevronLeft size={20} className="text-gray-600" />
                </button>

                <div className="flex-1 text-center">
                    <p className="text-sm font-bold text-gray-700">{template.name}</p>
                    <div className="flex items-center justify-center gap-1.5 mt-2">
                        {THUMBNAIL_TEMPLATES.map((_, idx) => (
                            <button
                                key={idx}
                                onClick={() => onTemplateChange(idx)}
                                className={`w-2 h-2 rounded-full transition-all ${idx === selectedTemplate ? 'bg-indigo-500 w-6' : 'bg-gray-300 hover:bg-gray-400'
                                    }`}
                            />
                        ))}
                    </div>
                </div>

                <button
                    onClick={() => onTemplateChange((selectedTemplate + 1) % THUMBNAIL_TEMPLATES.length)}
                    className="p-2.5 bg-white border-2 border-gray-200 rounded-xl hover:bg-gray-50 hover:border-indigo-300 transition-all"
                >
                    <ChevronRight size={20} className="text-gray-600" />
                </button>
            </div>
        </div>
    );
};

// Thumbnail Customization Panel - ChatGPT Style
export const ThumbnailCustomizationPanel = ({ headshots, onHeadshotsChange }) => {
    const [messages, setMessages] = useState([
        { id: 1, text: "Hi! I'm Mariko, your thumbnail design assistant. Upload your host and guest photos, then click Generate to create your YouTube thumbnail! \u{1F3A8}", isUser: false },
    ]);
    const [inputValue, setInputValue] = useState('');
    const [isTyping, setIsTyping] = useState(false);
    const messagesEndRef = useRef(null);
    const fileInputRef = useRef(null);
    const timeoutRef = useRef(null);

    const scrollToBottom = useCallback(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, []);

    useEffect(() => {
        scrollToBottom();
    }, [messages, isTyping, scrollToBottom]);

    // Cleanup timeout on unmount to prevent memory leaks
    useEffect(() => {
        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, []);

    const handleSend = useCallback(() => {
        if (!inputValue.trim()) return;

        setMessages(prev => [...prev, { id: Date.now(), text: inputValue, isUser: true }]);
        setInputValue('');
        setIsTyping(true);

        timeoutRef.current = setTimeout(() => {
            setIsTyping(false);
            const responses = [
                "I've adjusted the design! How does it look now?",
                "Great choice! The thumbnail is looking more engaging.",
                "Perfect! I've made those changes for you. \u2728",
            ];
            setMessages(prev => [...prev, {
                id: Date.now() + 1,
                text: responses[Math.floor(Math.random() * responses.length)],
                isUser: false
            }]);
        }, 1500);
    }, [inputValue]);

    const handleFileUpload = useCallback((event) => {
        const file = event.target.files?.[0];
        if (file) {
            const reader = new FileReader();
            reader.onload = (e) => {
                const imageUrl = e.target.result;
                const type = !headshots.user ? 'user' : 'guest';
                onHeadshotsChange({ ...headshots, [type]: imageUrl });
            };
            reader.readAsDataURL(file);
        }
    }, [headshots, onHeadshotsChange]);

    const handleKeyPress = useCallback((e) => {
        if (e.key === 'Enter') {
            handleSend();
        }
    }, [handleSend]);

    return (
        <div className="flex flex-col h-full bg-gradient-to-b from-slate-50 to-white">
            {/* Header */}
            <div className="p-6 border-b border-gray-200 flex-none">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center">
                        <Sparkles size={20} className="text-white" />
                    </div>
                    <div>
                        <h3 className="font-bold text-gray-900">Mariko AI</h3>
                        <p className="text-xs text-gray-500">Thumbnail Designer</p>
                    </div>
                </div>
            </div>

            {/* Messages Area */}
            <div className="flex-1 overflow-y-auto p-6 space-y-4 min-h-0">
                {messages.map((msg) => (
                    <div key={msg.id} className={`flex ${msg.isUser ? 'justify-end' : 'justify-start'}`}>
                        <div className={`max-w-[80%] ${msg.isUser
                            ? 'bg-gradient-to-r from-indigo-500 to-purple-600 text-white'
                            : 'bg-white border border-gray-200'
                            } rounded-2xl px-4 py-3 shadow-sm`}>
                            <p className={`text-sm ${msg.isUser ? 'text-white' : 'text-gray-700'}`}>{msg.text}</p>
                            {msg.image && (
                                <div className="mt-2">
                                    <img src={msg.image} alt={msg.imageLabel} className="w-16 h-16 rounded-lg object-cover border-2 border-white/20" />
                                    <p className="text-xs mt-1 opacity-75">{msg.imageLabel}</p>
                                </div>
                            )}
                        </div>
                    </div>
                ))}
                {isTyping && (
                    <div className="flex justify-start">
                        <div className="bg-white border border-gray-200 rounded-2xl px-4 py-3">
                            <div className="flex gap-1">
                                <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                                <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                                <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                            </div>
                        </div>
                    </div>
                )}
                <div ref={messagesEndRef} />
            </div>

            {/* Headshot Indicators - Small Silhouettes */}
            <div className="px-6 py-3 border-t border-gray-100 flex-none">
                <div className="flex items-center justify-center gap-4">
                    <div className="flex flex-col items-center">
                        <div className="relative">
                            <div className={`w-12 h-12 rounded-full flex items-center justify-center ${headshots.user ? 'bg-indigo-100 border-2 border-indigo-500' : 'bg-gray-100 border-2 border-dashed border-gray-300'
                                }`}>
                                {headshots.user ? (
                                    <img src={headshots.user} alt="Host" className="w-full h-full rounded-full object-cover" />
                                ) : (
                                    <User size={20} className="text-gray-400" />
                                )}
                            </div>
                            {headshots.user && (
                                <button
                                    onClick={() => onHeadshotsChange({ ...headshots, user: null })}
                                    className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 hover:bg-red-600 rounded-full flex items-center justify-center shadow-lg transition-colors"
                                >
                                    <X size={12} className="text-white" />
                                </button>
                            )}
                        </div>
                        <span className="text-[10px] text-gray-500 mt-1 font-medium">Host</span>
                    </div>
                    <div className="flex flex-col items-center">
                        <div className="relative">
                            <div className={`w-12 h-12 rounded-full flex items-center justify-center ${headshots.guest ? 'bg-purple-100 border-2 border-purple-500' : 'bg-gray-100 border-2 border-dashed border-gray-300'
                                }`}>
                                {headshots.guest ? (
                                    <img src={headshots.guest} alt="Guest" className="w-full h-full rounded-full object-cover" />
                                ) : (
                                    <User size={20} className="text-gray-400" />
                                )}
                            </div>
                            {headshots.guest && (
                                <button
                                    onClick={() => onHeadshotsChange({ ...headshots, guest: null })}
                                    className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 hover:bg-red-600 rounded-full flex items-center justify-center shadow-lg transition-colors"
                                >
                                    <X size={12} className="text-white" />
                                </button>
                            )}
                        </div>
                        <span className="text-[10px] text-gray-500 mt-1 font-medium">Guest</span>
                    </div>
                </div>
            </div>

            {/* Quick Actions */}
            <div className="px-6 py-3 border-t border-gray-100 flex-none">
                <div className="flex gap-2 flex-wrap">
                    {['Bigger text', 'Change colors', 'Reposition faces', 'Add emoji'].map((action) => (
                        <button
                            key={action}
                            onClick={() => {
                                setMessages(prev => [...prev, { id: Date.now(), text: action, isUser: true }]);
                                setIsTyping(true);
                                timeoutRef.current = setTimeout(() => {
                                    setIsTyping(false);
                                    setMessages(prev => [...prev, {
                                        id: Date.now() + 1,
                                        text: `I've ${action.toLowerCase()} for you! \u2728`,
                                        isUser: false
                                    }]);
                                }, 1000);
                            }}
                            className="px-3 py-1.5 text-xs font-medium text-indigo-600 bg-indigo-50 hover:bg-indigo-100 rounded-lg transition-colors"
                        >
                            {action}
                        </button>
                    ))}
                </div>
            </div>

            {/* Input Area */}
            <div className="p-6 border-t border-gray-200 flex-none">
                <div className="flex gap-2">
                    <input
                        type="file"
                        ref={fileInputRef}
                        onChange={handleFileUpload}
                        accept="image/*"
                        className="hidden"
                    />
                    <button
                        onClick={() => fileInputRef.current?.click()}
                        className="p-3 bg-white border-2 border-gray-200 rounded-xl hover:bg-gray-50 hover:border-indigo-300 transition-all"
                    >
                        <Image size={20} className="text-gray-600" />
                    </button>
                    <input
                        type="text"
                        value={inputValue}
                        onChange={(e) => setInputValue(e.target.value)}
                        onKeyPress={handleKeyPress}
                        placeholder="Describe your changes..."
                        className="flex-1 px-4 py-3 bg-white border-2 border-gray-200 rounded-xl focus:outline-none focus:border-indigo-500 transition-colors"
                    />
                    <button
                        onClick={handleSend}
                        disabled={!inputValue.trim()}
                        className="p-3 bg-gradient-to-r from-indigo-500 to-purple-600 text-white rounded-xl hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        <Send size={20} />
                    </button>
                </div>
            </div>
        </div>
    );
};

// YouTube Shorts Preview Component
export const YouTubeShortsPreview = () => {
    return (
        <div className="relative w-full h-full bg-black overflow-hidden font-sans">
            {/* Video Background */}
            <div className="absolute inset-0 bg-stone-900 flex items-center justify-center">
                <div className="w-full h-full opacity-40 bg-gradient-to-b from-stone-800 to-black animate-pulse" />
                <Play className="w-[12%] h-auto text-white/20 absolute" fill="white" />
            </div>

            {/* Top Bar */}
            <div className="absolute top-0 left-0 right-0 z-20 flex justify-between items-center px-4 pt-6 pb-2 bg-gradient-to-b from-black/60 to-transparent">
                <div className="flex items-center gap-4">
                    <span className="text-[2.2vh] font-bold tracking-tighter text-white drop-shadow-md font-sans">Shorts</span>
                </div>
                <div className="flex items-center gap-[1.5vw] text-white">
                    <Search className="w-[6%] max-w-[22px] h-auto" strokeWidth={2} />
                    <Camera className="w-[6%] max-w-[22px] h-auto" strokeWidth={2} />
                    <MoreHorizontal className="w-[6%] max-w-[22px] h-auto" strokeWidth={2} />
                </div>
            </div>

            {/* Right Side Actions */}
            <div className="absolute right-2 bottom-4 z-20 flex flex-col items-center gap-[2.5vh] w-[12%] pb-2">
                {/* Like */}
                <div className="flex flex-col items-center gap-1 cursor-pointer group w-full">
                    <div className="w-[80%] aspect-square flex items-center justify-center transition-transform active:scale-90">
                        <ThumbsUp className="w-[65%] h-auto text-white drop-shadow-md" fill="white" strokeWidth={1.5} />
                    </div>
                    <span className="text-[10px] font-medium text-white drop-shadow-md">24K</span>
                </div>

                {/* Dislike */}
                <div className="flex flex-col items-center gap-1 cursor-pointer group w-full">
                    <div className="w-[80%] aspect-square flex items-center justify-center transition-transform active:scale-90">
                        <ThumbsDown className="w-[65%] h-auto text-white drop-shadow-md" strokeWidth={1.5} />
                    </div>
                    <span className="text-[10px] font-medium text-white drop-shadow-md">Dislike</span>
                </div>

                {/* Comment */}
                <div className="flex flex-col items-center gap-1 cursor-pointer group w-full">
                    <div className="w-[80%] aspect-square flex items-center justify-center transition-transform active:scale-90">
                        <MessageCircle className="w-[65%] h-auto text-white drop-shadow-md" strokeWidth={1.5} fill="white" />
                    </div>
                    <span className="text-[10px] font-medium text-white drop-shadow-md">1.2K</span>
                </div>

                {/* Share */}
                <div className="flex flex-col items-center gap-1 cursor-pointer group w-full">
                    <div className="w-[80%] aspect-square flex items-center justify-center transition-transform active:scale-90">
                        <Share2 className="w-[65%] h-auto text-white drop-shadow-md" strokeWidth={1.5} fill="white" />
                    </div>
                    <span className="text-[10px] font-medium text-white drop-shadow-md">Share</span>
                </div>

                {/* Remix */}
                <div className="flex flex-col items-center gap-1 cursor-pointer group w-full">
                    <div className="w-[80%] aspect-square flex items-center justify-center transition-transform active:scale-90">
                        <Play className="w-[65%] h-auto text-white drop-shadow-md" strokeWidth={1.5} />
                    </div>
                    <span className="text-[10px] font-medium text-white drop-shadow-md">Remix</span>
                </div>

                {/* Sound/Album Art */}
                <div className="mt-2 w-[60%] aspect-square rounded-md overflow-hidden border-2 border-white/20 bg-stone-800">
                    <div className="w-full h-full bg-gradient-to-br from-red-500 to-orange-500 animate-spin-slow" />
                </div>
            </div>

            {/* Bottom Info Overlay */}
            <div className="absolute bottom-0 left-0 right-16 z-10 px-4 pb-4 pt-12 bg-gradient-to-t from-black/90 via-black/40 to-transparent">
                {/* Channel Info */}
                <div className="flex items-center gap-2 mb-3">
                    <div className="w-[8%] max-w-[32px] aspect-square rounded-full bg-gradient-to-tr from-blue-500 to-cyan-500 p-[1px]">
                        <div className="w-full h-full rounded-full bg-black flex items-center justify-center overflow-hidden">
                            <User className="w-[50%] h-auto text-white" />
                        </div>
                    </div>
                    <span className="text-white text-[13px] font-semibold">@podguestly</span>
                    <button className="bg-white text-black text-[11px] font-bold px-3 py-1.5 rounded-full hover:bg-gray-200 transition-colors">
                        Subscribe
                    </button>
                </div>

                {/* Caption */}
                <div className="mb-3">
                    <p className="text-white text-[13px] leading-snug line-clamp-2">
                        EP 42: Building the future with AI 🚀 <span className="font-semibold">#shorts #ai #podcast</span>
                    </p>
                </div>

                {/* Sound Info */}
                <div className="flex items-center gap-2 text-white/90">
                    <Music className="w-[4%] min-w-[14px] h-auto" />
                    <div className="overflow-hidden max-w-[200px]">
                        <p className="text-[12px] whitespace-nowrap animate-marquee">
                            Original Sound - PodGuestly \u00B7 Trending
                        </p>
                    </div>
                </div>
            </div>

            {/* Red Progress Bar */}
            <div className="absolute bottom-0 left-0 right-0 h-[3px] bg-white/20 z-20">
                <div className="h-full w-[65%] bg-[#FF0000]" />
            </div>
        </div>
    );
};

// Helper function to determine if content is YouTube-related
export const isYouTubeContent = (contentType) => {
    if (!contentType) return false;

    const id = contentType.id?.toLowerCase() || '';
    const name = contentType.name?.toLowerCase() || '';
    const platform = contentType.platform?.toLowerCase() || '';

    return (
        platform.includes('youtube') ||
        id.includes('youtube') ||
        id.includes('shorts') ||
        id.includes('thumbnail') ||
        name.includes('youtube') ||
        name.includes('shorts') ||
        name.includes('thumbnail')
    );
};

// Helper function to determine YouTube content subtype
export const getYouTubeContentType = (contentType) => {
    if (!contentType) return null;

    const id = contentType.id?.toLowerCase() || '';
    const name = contentType.name?.toLowerCase() || '';

    // Shorts content types
    if (id.includes('shorts') || name.includes('shorts')) {
        return 'shorts';
    }

    // Thumbnail content types
    if (id.includes('thumbnail') || name.includes('thumbnail')) {
        return 'thumbnail';
    }

    // Highlight clip (longer YouTube video)
    if (id.includes('highlight') || name.includes('highlight')) {
        return 'highlight';
    }

    // Default for YouTube platform
    if (contentType.platform === 'YouTube') {
        return 'shorts';
    }

    return null;
};

export default {
    THUMBNAIL_TEMPLATES,
    YouTubeThumbnailPreview,
    ThumbnailCustomizationPanel,
    YouTubeShortsPreview,
    isYouTubeContent,
    getYouTubeContentType
};